import React from 'react'
import { Flex } from '@common/fundamentals'
import { Button } from '@common/material'
import { Check, Close } from '@mui/icons-material'
import { localizedStrings } from '@core/strings'
import { Palette } from '@core/theme/color-palette-siteassist'

export type AppraisalStatus = 'PASS' | 'FAIL' | 'NA'

const appraisalButtonConfig: Record<
  AppraisalStatus,
  {
    icon: React.ComponentType<{ sx?: object }> | null
    label?: string
    selectedColor: string
    selectedBgColor: string
    textColor: string
    selectedTextColor: string
    hoverBgColor: string
    hoverSelectedBgColor: string
    backgroundColor: string
    borderColor: string
  }
> = {
  PASS: {
    icon: Check,
    selectedColor: 'success.main',
    selectedBgColor: 'success.main',
    textColor: 'success.main',
    selectedTextColor: Palette.Common.White,
    hoverBgColor: 'success.light',
    hoverSelectedBgColor: 'success.dark',
    backgroundColor: Palette.Green[50],
    borderColor: Palette.Green[400],
  },
  FAIL: {
    icon: Close,
    selectedColor: 'error.main',
    selectedBgColor: 'error.main',
    textColor: 'error.main',
    selectedTextColor: Palette.Common.White,
    hoverBgColor: 'error.light',
    hoverSelectedBgColor: 'error.dark',
    backgroundColor: Palette.Red[50],
    borderColor: 'error.main',
  },
  NA: {
    icon: null,
    label: localizedStrings.notApplicable,
    selectedColor: 'grey.500',
    selectedBgColor: 'grey.500',
    textColor: 'text.secondary',
    selectedTextColor: 'text.secondary',
    hoverBgColor: Palette.Grey[400],
    hoverSelectedBgColor: Palette.Grey[600],
    backgroundColor: Palette.Grey[200],
    borderColor: Palette.Grey[500],
  },
}

export type AppraisalStatusButtonsProps = {
  value: AppraisalStatus | null
  onChange: (status: AppraisalStatus) => void
  variant?: 'document' | 'query'
  size?: 'medium' | 'small'
  isDocumentAppraised?: boolean
}

const documentStatuses: AppraisalStatus[] = ['PASS', 'FAIL']
const queryStatuses: AppraisalStatus[] = ['PASS', 'FAIL', 'NA']

export const AppraisalStatusButtons: React.FC<AppraisalStatusButtonsProps> = ({
  value,
  onChange,
  variant = 'document',
  size = 'medium',
  isDocumentAppraised = false,
}) => {
  const statuses = variant === 'document' ? documentStatuses : queryStatuses

  return (
    <Flex gap={2}>
      {statuses.map((status) => {
        const config = appraisalButtonConfig[status]
        const isSelected = value === status
        const Icon = config.icon

        return (
          <Button
            key={status}
            variant={isSelected ? 'contained' : 'outlined'}
            onClick={() => onChange(status)}
            disabled={isDocumentAppraised}
            size={size}
            sx={{
              flex: 1,
              borderRadius: 6,
              py: size === 'small' ? 0.75 : 1,
              maxHeight: size === 'small' ? 32 : undefined,
              borderColor: isSelected
                ? config.selectedColor
                : config.borderColor,
              backgroundColor: isSelected
                ? config.selectedBgColor
                : config.backgroundColor,
              color: isSelected ? config.selectedTextColor : config.textColor,
              '&:hover': {
                backgroundColor: isSelected
                  ? config.hoverSelectedBgColor
                  : config.hoverBgColor,
                borderColor: config.selectedColor,
              },
              '&.Mui-disabled': {
                opacity: isSelected ? 1 : 0.5,
                color: isSelected ? config.selectedTextColor : config.textColor,
                backgroundColor: isSelected
                  ? config.selectedBgColor
                  : config.backgroundColor,
                borderColor: isSelected
                  ? config.selectedColor
                  : config.borderColor,
              },
            }}
          >
            {Icon ? (
              <Icon
                sx={size === 'small' ? { height: 20, width: 20 } : undefined}
              />
            ) : (
              config.label
            )}
          </Button>
        )
      })}
    </Flex>
  )
}
